import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Context, Lijst } from '@xxtract/tabelfilters';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { Filterbalk, SorteerbareKop, ariaSort, useStartadres, useTabelData, useTabelStand } from '@/components/tabel';
import { getPageViews, type AnalyticsAggregate } from '../analytics.api';

function isoDay(daysAgo: number): string { return new Date(Date.now() - daysAgo * 86_400_000).toISOString().slice(0, 10); }

/** Kolommen van Paginaweergaven (spec-tabelfilters story 5); weergaven is een getal: sorteerbaar, niet filterbaar. */
export const PAGINAWEERGAVEN_LIJST: Lijst<AnalyticsAggregate> = {
  kolommen: [
    { sleutel: 'route', kopSleutel: 'analytics.route', type: 'tekst', waarde: (r) => r.route },
    { sleutel: 'rol', kopSleutel: 'analytics.role', type: 'keuzelijst', waarde: (r) => r.role, waardeLabel: (w, t) => t(`analytics.roles.${w}`) },
    { sleutel: 'dag', kopSleutel: 'analytics.day', type: 'datum', waarde: (r) => r.day },
    // ponytail: Number() loses precision above 2^53 views; only the sort order uses it, the cell shows the text.
    { sleutel: 'weergaven', kopSleutel: 'analytics.views', type: 'getal', waarde: (r) => Number(r.count) },
  ],
};
const STAND_SLEUTEL = 'paginaweergaven';
const FILTER_TEST_ID = 'analytics';
const STARTADRES = [{ lijst: PAGINAWEERGAVEN_LIJST, standSleutel: STAND_SLEUTEL }];

export function AnalyticsPage() {
  const { t } = useTranslation();
  const from = isoDay(29); const to = isoDay(0);
  const query = useQuery({ queryKey: ['analytics', 'pageviews', from, to], queryFn: () => getPageViews(from, to) });
  const context = useMemo<Context>(() => ({ t }), [t]);
  const { start, geweigerd, wisGeweigerd } = useStartadres(STARTADRES);
  const stand = useTabelStand(PAGINAWEERGAVEN_LIJST, STAND_SLEUTEL, { start: start[STAND_SLEUTEL] });
  const tabel = useTabelData(query.data, PAGINAWEERGAVEN_LIJST, stand, context);
  const kopRef = useRef<HTMLHeadingElement>(null);

  return <section data-testid="analytics-page">
    <h1 className="text-xl font-bold text-[var(--foreground-heading)] outline-none" ref={kopRef} tabIndex={-1}>{t('analytics.title')}</h1>
    <p className="mt-1 text-sm text-[var(--foreground-muted)]">{t('analytics.description')}</p>
    {/* Not while loading: that state is the page's only `status` region. */}
    {!query.isLoading && <FilterRejectedMessage baseTestId={FILTER_TEST_ID} focusNaWissen={kopRef} linkGeweigerd={geweigerd} onClear={() => { stand.reset(); wisGeweigerd(); }} />}
    {query.isLoading && <p className="mt-5" role="status">{t('common.loading')}</p>}
    {query.isError && <p className="mt-5" role="alert">{t('analytics.error')}</p>}
    {query.data?.length === 0 && <p className="mt-5">{t('analytics.empty')}</p>}
    {/* The table headers already stand while loading; the filters follow with the data (their choices come from it). */}
    {!query.isError && query.data?.length !== 0 && <>
      {query.data && <div className="mt-5"><Filterbalk context={context} data={tabel} lijst={PAGINAWEERGAVEN_LIJST} stand={stand} /></div>}
      <div className="mt-3 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>{PAGINAWEERGAVEN_LIJST.kolommen.map((k) => <TableHead aria-label={t(k.kopSleutel)} aria-sort={ariaSort(stand.sortering, k.sleutel)} key={k.sleutel}><SorteerbareKop kolom={k} onKlik={() => stand.klikOpKop(k.sleutel)} sortering={stand.sortering} /></TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {query.data && tabel.rijen.length === 0 && <TableRow><TableCell className="py-8 text-center text-[var(--foreground-muted)]" colSpan={PAGINAWEERGAVEN_LIJST.kolommen.length}>{t('common.noResults')}</TableCell></TableRow>}
            {tabel.rijen.map((row) => <TableRow key={`${row.route}-${row.role}-${row.day}`}><TableCell>{row.route}</TableCell><TableCell>{t(`analytics.roles.${row.role}`)}</TableCell><TableCell>{row.day}</TableCell><TableCell>{row.count}</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </div>
    </>}
  </section>;
}
